import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {ArrowLeft, Save, Trash2, User, Building, Mail, Phone, MapPin, Calendar, Tag, CreditCard, History, Shield, Lock} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import RecurringPaymentSchedules from '../components/payments/RecurringPaymentSchedules';
import HistoricalPayments from '../components/payments/HistoricalPayments';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CONTACT_TYPES = [
  'Client',
  'Prospect',
  'Contact',
  'Opposing Party',
  'Attorney',
  'Judge',
  'Witness',
  'Expert',
  'Referral'
];

const PREFIXES = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Hon.', 'Prof.'];
const SUFFIXES = ['Jr.', 'Sr.', 'II', 'III', 'IV', 'Esq.', 'MD', 'PhD'];
const CLIENT_STATUSES = [
  { value: '', label: 'Not a Client' },
  { value: 'active', label: 'Active Client' },
  { value: 'prospective', label: 'Prospective Client' },
  { value: 'former', label: 'Former Client' }
];

function ContactForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'attorney';
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [staffPin, setStaffPin] = useState('');
  const [staffPinConfirm, setStaffPinConfirm] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [pinMessage, setPinMessage] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    contact_type: 'Client',
    first_name: '',
    last_name: '',
    middle_name: '',
    prefix: '',
    suffix: '',
    company: '',
    title: '',
    primary_email_address: '',
    secondary_email_address: '',
    primary_phone_number: '',
    secondary_phone_number: '',
    primary_address_street: '',
    primary_address_city: '',
    primary_address_province: '',
    primary_address_postal_code: '',
    primary_address_country: 'USA',
    secondary_address_street: '',
    secondary_address_city: '',
    secondary_address_province: '',
    secondary_address_postal_code: '',
    secondary_address_country: '',
    date_of_birth: '',
    tags: [],
    is_client: true,
    client_status: 'active',
    portal_enabled: false
  });

  useEffect(() => {
    if (isEditing) {
      fetchContact();
    }
  }, [id]);

  const fetchContact = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const contact = response.data;
      setFormData({
        contact_type: contact.contact_type || contact.entity || 'Client',
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        middle_name: contact.middle_name || '',
        prefix: contact.prefix || '',
        suffix: contact.suffix || '',
        company: contact.company || '',
        title: contact.title || '',
        primary_email_address: contact.primary_email_address || '',
        secondary_email_address: contact.secondary_email_address || '',
        primary_phone_number: contact.primary_phone_number || '',
        secondary_phone_number: contact.secondary_phone_number || '',
        primary_address_street: contact.primary_address_street || '',
        primary_address_city: contact.primary_address_city || '',
        primary_address_province: contact.primary_address_province || '',
        primary_address_postal_code: contact.primary_address_postal_code || '',
        primary_address_country: contact.primary_address_country || 'USA',
        secondary_address_street: contact.secondary_address_street || '',
        secondary_address_city: contact.secondary_address_city || '',
        secondary_address_province: contact.secondary_address_province || '',
        secondary_address_postal_code: contact.secondary_address_postal_code || '',
        secondary_address_country: contact.secondary_address_country || '',
        date_of_birth: contact.date_of_birth ? contact.date_of_birth.split('T')[0] : '',
        tags: contact.tags || [],
        is_client: contact.is_client || false,
        client_status: contact.client_status || '',
        portal_enabled: contact.portal_enabled || false
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching contact:', err);
      setError('Failed to load contact');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-set is_client when contact_type is Client
    if (name === 'contact_type' && value === 'Client') {
      setFormData(prev => ({ ...prev, is_client: true, client_status: prev.client_status || 'active' }));
    }

    // Auto-set is_client based on client_status
    if (name === 'client_status') {
      setFormData(prev => ({ ...prev, is_client: value !== '' }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError('First name and last name are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('token');

      // Prepare data - convert empty strings to null for optional fields
      const submitData = {
        ...formData,
        middle_name: formData.middle_name || null,
        prefix: formData.prefix || null,
        suffix: formData.suffix || null,
        company: formData.company || null,
        title: formData.title || null,
        primary_email_address: formData.primary_email_address || null,
        secondary_email_address: formData.secondary_email_address || null,
        primary_phone_number: formData.primary_phone_number || null,
        secondary_phone_number: formData.secondary_phone_number || null,
        primary_address_street: formData.primary_address_street || null,
        primary_address_city: formData.primary_address_city || null,
        primary_address_province: formData.primary_address_province || null,
        primary_address_postal_code: formData.primary_address_postal_code || null,
        primary_address_country: formData.primary_address_country || null,
        secondary_address_street: formData.secondary_address_street || null,
        secondary_address_city: formData.secondary_address_city || null,
        secondary_address_province: formData.secondary_address_province || null,
        secondary_address_postal_code: formData.secondary_address_postal_code || null,
        secondary_address_country: formData.secondary_address_country || null,
        date_of_birth: formData.date_of_birth || null,
        client_status: formData.client_status || null
      };

      if (isEditing) {
        await axios.put(`${API_URL}/contacts/${id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/contacts/`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      navigate('/contacts');
    } catch (err) {
      console.error('Error saving contact:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to save contact';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  const handleDeleteContact = async () => {
    if (!window.confirm(`Are you sure you want to delete ${formData.first_name} ${formData.last_name}? This action can be undone by an administrator.`)) {
      return;
    }
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/contacts');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete contact');
      setDeleting(false);
    }
  };

  const handleSetPin = async () => {
    if (!/^\d{6}$/.test(staffPin)) {
      setPinMessage('PIN must be exactly 6 digits');
      return;
    }
    if (staffPin !== staffPinConfirm) {
      setPinMessage('PINs do not match');
      return;
    }
    setPinSaving(true);
    setPinMessage('');
    try {
      await axios.post(`/api/v1/contacts/${id}/set-pin`, { pin: staffPin });
      setFormData(prev => ({ ...prev, has_pin: true }));
      setShowPinModal(false);
      setStaffPin('');
      setStaffPinConfirm('');
      setPinMessage('');
    } catch (err) {
      setPinMessage(err.response?.data?.detail || 'Failed to set PIN');
    } finally {
      setPinSaving(false);
    }
  };


  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/contacts')}
          className="p-2 hover:bg-virgo-navy-600 rounded-lg transition"
        >
          <ArrowLeft className="w-6 h-6 text-virgo-teal-400" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-virgo-teal-100">
            {isEditing ? 'Edit Contact' : 'New Contact'}
          </h1>
          <p className="text-virgo-teal-300 mt-1">
            {isEditing ? 'Update contact information' : 'Add a new contact to your directory'}
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Contact Type & Basic Info */}
        <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-virgo-teal-400" />
            <h2 className="text-lg font-semibold text-virgo-teal-100">Basic Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Contact Type *
              </label>
              <select
                name="contact_type"
                value={formData.contact_type}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              >
                {/* legacy free-text values stay selectable so the field is never blank */}
                {(CONTACT_TYPES.includes(formData.contact_type) || !formData.contact_type
                  ? CONTACT_TYPES
                  : [formData.contact_type, ...CONTACT_TYPES]
                ).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {id && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-virgo-teal-400">Quick status override (audited):</span>
                  {[['prospect', 'Prospect'], ['client', 'Client'], ['former_client', 'Former client']].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          await axios.patch(`${API_URL}/contacts/${id}/status`, { status: value },
                            { headers: { Authorization: `Bearer ${token}` } });
                          window.location.reload();
                        } catch (err) {
                          alert(err.response?.data?.detail || 'Failed to set status');
                        }
                      }}
                      className="px-2 py-1 text-xs bg-virgo-navy-800 text-virgo-teal-300 border border-virgo-teal-700 rounded hover:bg-virgo-navy-600 transition-colors"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Prefix
              </label>
              <select
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              >
                <option value="">None</option>
                {PREFIXES.map(prefix => (
                  <option key={prefix} value={prefix}>{prefix}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Middle Name
              </label>
              <input
                type="text"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Suffix
              </label>
              <select
                name="suffix"
                value={formData.suffix}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              >
                <option value="">None</option>
                {SUFFIXES.map(suffix => (
                  <option key={suffix} value={suffix}>{suffix}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Organization */}
        <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-5 h-5 text-virgo-teal-400" />
            <h2 className="text-lg font-semibold text-virgo-teal-100">Organization</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Company
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-virgo-teal-400" />
            <h2 className="text-lg font-semibold text-virgo-teal-100">Contact Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Primary Email
              </label>
              <input
                type="email"
                name="primary_email_address"
                value={formData.primary_email_address}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Secondary Email
              </label>
              <input
                type="email"
                name="secondary_email_address"
                value={formData.secondary_email_address}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Primary Phone
              </label>
              <input
                type="tel"
                name="primary_phone_number"
                value={formData.primary_phone_number}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Secondary Phone
              </label>
              <input
                type="tel"
                name="secondary_phone_number"
                value={formData.secondary_phone_number}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Primary Address */}
        <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-virgo-teal-400" />
            <h2 className="text-lg font-semibold text-virgo-teal-100">Primary Address</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Street Address
              </label>
              <input
                type="text"
                name="primary_address_street"
                value={formData.primary_address_street}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                City
              </label>
              <input
                type="text"
                name="primary_address_city"
                value={formData.primary_address_city}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                State/Province
              </label>
              <input
                type="text"
                name="primary_address_province"
                value={formData.primary_address_province}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Postal Code
              </label>
              <input
                type="text"
                name="primary_address_postal_code"
                value={formData.primary_address_postal_code}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Country
              </label>
              <input
                type="text"
                name="primary_address_country"
                value={formData.primary_address_country}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Secondary Address */}
        <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-virgo-teal-400" />
            <h2 className="text-lg font-semibold text-virgo-teal-100">Secondary Address</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Street Address
              </label>
              <input
                type="text"
                name="secondary_address_street"
                value={formData.secondary_address_street}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                City
              </label>
              <input
                type="text"
                name="secondary_address_city"
                value={formData.secondary_address_city}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                State/Province
              </label>
              <input
                type="text"
                name="secondary_address_province"
                value={formData.secondary_address_province}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Postal Code
              </label>
              <input
                type="text"
                name="secondary_address_postal_code"
                value={formData.secondary_address_postal_code}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Country
              </label>
              <input
                type="text"
                name="secondary_address_country"
                value={formData.secondary_address_country}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Tags & Settings */}
        <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-5 h-5 text-virgo-teal-400" />
            <h2 className="text-lg font-semibold text-virgo-teal-100">Tags & Settings</h2>
          </div>

          <div className="space-y-4">
            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                Tags
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Add a tag..."
                  className="flex-1 px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition"
                >
                  Add
                </button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-virgo-navy-600 text-virgo-teal-200 rounded-full text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-red-400"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Client Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                  Client Status
                </label>
                <select
                  name="client_status"
                  value={formData.client_status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  {CLIENT_STATUSES.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
                <p className="text-virgo-teal-500 text-xs mt-1">
                  {formData.client_status === 'active' && 'Currently an active client of the firm'}
                  {formData.client_status === 'prospective' && 'Potential client being evaluated'}
                  {formData.client_status === 'former' && 'Previously represented by the firm'}
                  {!formData.client_status && 'Not a client (e.g., opposing counsel, witness)'}
                </p>
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 text-virgo-teal-200">
                  <input
                    type="checkbox"
                    name="portal_enabled"
                    checked={formData.portal_enabled}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-virgo-teal-700 text-virgo-teal-600 focus:ring-virgo-teal-500"
                  />
                  Enable Client Portal Access
                </label>
              
            </div>

            {/* Security PIN */}
            {isEditing && formData.is_client && (
              <div className="mt-4 pt-4 border-t border-virgo-teal-700/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-virgo-teal-400" />
                    <span className="text-sm text-virgo-teal-300">Security PIN</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${formData.has_pin ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {formData.has_pin ? 'Set' : 'Not Set'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowPinModal(true); setStaffPin(''); setStaffPinConfirm(''); setPinMessage(''); }}
                    className="px-3 py-1 bg-virgo-teal-600/30 text-virgo-teal-300 rounded-lg hover:bg-virgo-teal-600/50 transition text-sm"
                  >
                    {formData.has_pin ? 'Reset PIN' : 'Set PIN'}
                  </button>
                </div>
                <p className="text-xs text-virgo-teal-500 mt-1">6-digit PIN used for phone identity verification</p>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Recurring Payments - Only show for existing clients */}
        {isEditing && formData.is_client && (
          <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-virgo-teal-400" />
              <h2 className="text-lg font-semibold text-virgo-teal-100">Recurring Payments</h2>
            </div>
            <RecurringPaymentSchedules contactId={id} />
          </div>
        )}

        {/* Historical Payments - Only show for existing clients */}
        {isEditing && formData.is_client && (
          <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-virgo-teal-400" />
              <h2 className="text-lg font-semibold text-virgo-teal-100">Payment History (Pre-Virgo)</h2>
            </div>
            <HistoricalPayments contactId={id} />
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex justify-between">
          {isEditing && isAdmin ? (
            <button
              type="button"
              onClick={handleDeleteContact}
              disabled={deleting}
              className="flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-600/40 transition disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              {deleting ? 'Deleting...' : 'Delete Contact'}
            </button>
          ) : <div />}
          <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate('/contacts')}
            className="px-6 py-2 bg-virgo-navy-600 text-virgo-teal-200 rounded-lg hover:bg-virgo-navy-500 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditing ? 'Update Contact' : 'Create Contact'}
              </>
            )}
          </button>
          </div>
        </div>
      </form>
    
      {/* PIN Modal */}
      {showPinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-virgo-navy-700 rounded-xl border border-virgo-teal-700/30 p-6 w-80">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-virgo-teal-400" />
              <h3 className="text-lg font-semibold text-white">{formData.has_pin ? 'Reset Client PIN' : 'Set Client PIN'}</h3>
            </div>
            {pinMessage && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-2 mb-3 text-red-300 text-sm">
                {pinMessage}
              </div>
            )}
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-virgo-teal-300 mb-1">6-Digit PIN</label>
                <input
                  type="password"
                  value={staffPin}
                  onChange={(e) => setStaffPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  maxLength={6}
                  inputMode="numeric"
                  placeholder="Enter PIN"
                  className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white text-center text-lg tracking-widest focus:ring-2 focus:ring-virgo-teal-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs text-virgo-teal-300 mb-1">Confirm PIN</label>
                <input
                  type="password"
                  value={staffPinConfirm}
                  onChange={(e) => setStaffPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  maxLength={6}
                  inputMode="numeric"
                  placeholder="Confirm PIN"
                  className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white text-center text-lg tracking-widest focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleSetPin}
                  disabled={pinSaving || staffPin.length !== 6}
                  className="flex-1 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition text-sm disabled:opacity-50"
                >
                  {pinSaving ? 'Saving...' : 'Save PIN'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPinModal(false)}
                  className="flex-1 py-2 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-500 transition text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
</div>
  );
}

export default ContactForm;
